import React, { useState } from 'react';
import { Avatar, Box, ButtonBase, Container, IconButton, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import LuggageOutlinedIcon from '@mui/icons-material/LuggageOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useAuth } from '../../auth/AuthContext';
import { displayFont, elevation } from '../../theme';

/**
 * The signed-in frame. Desktop: a light top bar with the wordmark, a pill nav
 * and your initial. Phone: a slim top bar and a bottom tab bar (Trips, New,
 * Profile), the pattern travellers know from every travel app.
 *
 * `wide` drops the reading-width container; wide pages set their own max width.
 */
export default function AppShell({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  const { session, signOut } = useAuth();
  const { pathname } = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const email = session?.user.email ?? '';

  const tabs = [
    { to: '/trips', label: 'Trips', icon: <LuggageOutlinedIcon />, active: pathname === '/trips' || (pathname.startsWith('/trips/') && pathname !== '/trips/new') || pathname.startsWith('/stops/') },
    { to: '/trips/new', label: 'New trip', icon: <AddCircleOutlineIcon />, active: pathname === '/trips/new' },
    { to: '/profile', label: 'Profile', icon: <PersonOutlineIcon />, active: pathname.startsWith('/profile') },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: { xs: session ? '88px' : 0, md: 0 } }}>
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 2.5, md: 5 },
          height: { xs: 56, md: 68 },
          bgcolor: 'rgba(245,242,238,0.86)',
          backdropFilter: 'saturate(1.4) blur(14px)',
        }}
      >
        <Typography
          component={Link}
          to="/trips"
          sx={{ fontFamily: displayFont, fontWeight: 700, fontSize: { xs: '1.35rem', md: '1.55rem' }, color: 'primary.main', textDecoration: 'none' }}
        >
          Roamoir
        </Typography>
        {session ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="nav"
              aria-label="Main"
              sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, p: 0.5, borderRadius: 999, bgcolor: '#FFFFFF', boxShadow: elevation.card }}
            >
              {tabs.map((t) => (
                <ButtonBase
                  key={t.to}
                  component={Link}
                  to={t.to}
                  aria-current={t.active ? 'page' : undefined}
                  sx={{
                    px: 2,
                    height: 38,
                    borderRadius: 999,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: t.active ? '#FFFFFF' : 'text.secondary',
                    bgcolor: t.active ? 'primary.main' : 'transparent',
                    transition: 'background-color 150ms',
                    '&:hover': { bgcolor: t.active ? 'primary.main' : 'rgba(26,58,42,0.06)' },
                  }}
                >
                  {t.label}
                </ButtonBase>
              ))}
            </Box>
            <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label="Account">
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.9rem', fontWeight: 700 }}>
                {(email[0] ?? 'R').toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchor}
              open={!!anchor}
              onClose={() => setAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled>
                <ListItemText primary="Signed in as" secondary={email} />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  signOut();
                }}
              >
                Sign out
              </MenuItem>
            </Menu>
          </Box>
        ) : null}
      </Box>

      <Container
        maxWidth={wide ? false : 'md'}
        disableGutters={wide}
        sx={{ py: wide ? { xs: 1, md: 2 } : 4, px: wide ? { xs: 2, md: 5 } : undefined }}
      >
        {children}
      </Container>

      {session ? (
        <Box
          component="nav"
          aria-label="Main"
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            left: 12,
            right: 12,
            bottom: 'calc(12px + env(safe-area-inset-bottom))',
            zIndex: 20,
            justifyContent: 'space-around',
            height: 64,
            borderRadius: 999,
            bgcolor: '#FFFFFF',
            boxShadow: elevation.raised,
          }}
        >
          {tabs.map((t) => (
            <ButtonBase
              key={t.to}
              component={Link}
              to={t.to}
              aria-current={t.active ? 'page' : undefined}
              sx={{
                flex: 1,
                flexDirection: 'column',
                gap: 0.25,
                borderRadius: 999,
                color: t.active ? 'primary.main' : 'text.secondary',
                fontSize: '0.7rem',
                fontWeight: t.active ? 700 : 500,
                '& svg': { fontSize: 24 },
              }}
            >
              {t.icon}
              {t.label}
            </ButtonBase>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
